import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import SignaturePad from "@/components/SignaturePad";
import { Plus, Trash, PaperPlaneTilt, Truck, CheckCircle, Circle, Signature as SignIcon } from "@phosphor-icons/react";

export default function ProductionPanel({ projectId }) {
  const { hasRole } = useAuth();
  const [pos, setPos] = useState([]);
  const [recs, setRecs] = useState([]);
  const [readyDate, setReadyDate] = useState("");
  const canManage = hasRole("pm", "inginer", "admin");

  const load = () => {
    api.get(`/projects/${projectId}/purchase-orders`).then((r) => setPos(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/receptions`).then((r) => setRecs(r.data)).catch(() => {});
    api.get(`/projects/${projectId}`).then((r) => setReadyDate(r.data?.ready_ship_date ? r.data.ready_ship_date.slice(0, 10) : "")).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const saveReadyDate = async () => {
    try {
      await api.put(`/projects/${projectId}`, { ready_ship_date: readyDate ? new Date(readyDate).toISOString() : null, r05_notified: false, r05_notified_days: [] });
      toast.success("Dată Ready-to-Ship salvată (R05 se va anunța cu 7 zile înainte)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="production-panel">
      <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-6" data-testid="r05-panel">
        <h3 className="font-head text-base font-bold">Ready-to-Ship (R05)</h3>
        <p className="mb-3 text-xs text-muted-foreground">Data estimată de finalizare a producției. Cu 7 zile înainte se anunță automat Asistent Vânzări, PM, Șef Montaj și Logistică.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs">Dată finalizare producție</Label>
            <Input type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} className="w-48" data-testid="ready-ship-date" disabled={!canManage} />
          </div>
          {canManage && <Button variant="outline" onClick={saveReadyDate} data-testid="save-ready-date-btn">Salvează</Button>}
        </div>
      </div>
      <PurchaseOrders projectId={projectId} pos={pos} reload={load} canManage={canManage} />
      <Receptions projectId={projectId} recs={recs} reload={load} canManage={canManage} />
    </div>
  );
}

function PurchaseOrders({ projectId, pos, reload, canManage }) {
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState({ name: "", cif: "", address: "" });
  const [term, setTerm] = useState("");
  const [lines, setLines] = useState([{ name: "", um: "buc", qty: 1, unit_price: 0 }]);
  const [emailFor, setEmailFor] = useState(null);
  const [rcpt, setRcpt] = useState("");

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const save = async () => {
    if (!supplier.name.trim()) return toast.error("Numele furnizorului e obligatoriu");
    try {
      await api.post(`/projects/${projectId}/purchase-orders`, {
        project_id: projectId, supplier, delivery_term: term, vat_rate: 19,
        lines: lines.map((l) => ({ ...l, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0 })),
      });
      toast.success("Comandă emisă");
      setOpen(false); setSupplier({ name: "", cif: "", address: "" }); setTerm("");
      setLines([{ name: "", um: "buc", qty: 1, unit_price: 0 }]);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendEmail = async () => {
    try {
      const { data } = await api.post(`/purchase-orders/${emailFor}/send-email`, { recipient: rcpt });
      toast[data.ok ? "success" : "error"](data.ok ? "Trimis furnizorului" : "Trimitere eșuată");
      setEmailFor(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-head text-lg font-bold">Comenzi materii prime</h3>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-po-btn"><Plus size={15} className="mr-1" /> Comandă</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Comandă emisă materii prime</DialogTitle></DialogHeader>
              <div className="grid max-h-[65vh] gap-3 overflow-y-auto pr-1">
                <div className="grid grid-cols-3 gap-2">
                  <div className="grid gap-1.5"><Label>Furnizor *</Label><Input value={supplier.name} onChange={(e) => setSupplier({ ...supplier, name: e.target.value })} data-testid="po-supplier" /></div>
                  <div className="grid gap-1.5"><Label>CIF</Label><Input value={supplier.cif} onChange={(e) => setSupplier({ ...supplier, cif: e.target.value })} /></div>
                  <div className="grid gap-1.5"><Label>Termen livrare</Label><Input value={term} onChange={(e) => setTerm(e.target.value)} /></div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="mb-2 flex items-center justify-between"><span className="text-xs font-bold uppercase text-muted-foreground">Articole</span>
                    <Button size="sm" variant="outline" className="h-7" onClick={() => setLines((l) => [...l, { name: "", um: "buc", qty: 1, unit_price: 0 }])}><Plus size={13} /></Button></div>
                  {lines.map((l, i) => (
                    <div key={i} className="mb-2 grid grid-cols-12 gap-2">
                      <Input className="col-span-5" placeholder="Denumire" value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} />
                      <Input className="col-span-2" placeholder="UM" value={l.um} onChange={(e) => setLine(i, "um", e.target.value)} />
                      <Input className="col-span-2" type="number" placeholder="Cant" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                      <Input className="col-span-2" type="number" placeholder="Preț" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                      <button className="col-span-1 text-muted-foreground hover:text-red-400" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}><Trash size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-po-btn">Emite comanda</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="space-y-2">
        {pos.map((po) => (
          <div key={po.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`po-${po.id}`}>
            <div className="flex-1">
              <span className="font-mono text-xs text-muted-foreground">{po.number}</span>
              <span className="ml-2 text-sm font-semibold">{po.supplier?.name}</span>
              <span className={cn("ml-2 rounded px-1.5 py-0.5 text-[10px]", po.status === "confirmata" ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-muted-foreground")}>{po.status}</span>
            </div>
            <span className="text-xs">{po.total_with_vat} lei</span>
            {canManage && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEmailFor(po.id); setRcpt(""); }}><PaperPlaneTilt size={13} className="mr-1" /> Trimite</Button>}
          </div>
        ))}
        {pos.length === 0 && <p className="text-sm text-muted-foreground">Nicio comandă emisă.</p>}
      </div>

      <Dialog open={!!emailFor} onOpenChange={(v) => !v && setEmailFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Trimite comanda furnizorului</DialogTitle></DialogHeader>
          <Input type="email" placeholder="furnizor@email.ro" value={rcpt} onChange={(e) => setRcpt(e.target.value)} data-testid="po-email" />
          <DialogFooter><Button onClick={sendEmail} disabled={!rcpt}>Trimite (Resend)</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Receptions({ projectId, recs, reload, canManage }) {
  const [signFor, setSignFor] = useState(null); // {rid, index, role_label}
  const [signName, setSignName] = useState("");
  const [signData, setSignData] = useState(null);
  const [signing, setSigning] = useState(false);

  const create = async () => {
    try {
      await api.post(`/projects/${projectId}/receptions`, { project_id: projectId, items: [] });
      toast.success("Recepție creată");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openSign = (rid, index, role_label) => { setSignFor({ rid, index, role_label }); setSignName(""); setSignData(null); };

  const confirmSign = async () => {
    if (!signName.trim()) return toast.error("Introdu numele semnatarului");
    setSigning(true);
    try {
      let sigUrl = null;
      if (signData) {
        const { data } = await api.post("/upload-base64", { data: signData, ext: "png" });
        sigUrl = data.path;
      }
      await api.post(`/receptions/${signFor.rid}/sign`, { index: signFor.index, name: signName, signature_url: sigUrl });
      toast.success("Semnătură înregistrată");
      setSignFor(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  const ship = async (rid) => {
    try { await api.post(`/receptions/${rid}/ready-to-ship`); toast.success("Ready to Ship ✔"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-head text-lg font-bold">Recepție CTC (3 semnături)</h3>
        {canManage && <Button size="sm" onClick={create} data-testid="add-rec-btn"><Plus size={15} className="mr-1" /> Recepție</Button>}
      </div>
      <div className="space-y-3">
        {recs.map((r) => {
          const allSigned = (r.signatures || []).every((s) => s.signed);
          return (
            <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`rec-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-muted-foreground">{r.number} · {fmtDate(r.date)}</span>
                {r.ready_to_ship && <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-400"><Truck size={12} /> READY TO SHIP</span>}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {(r.signatures || []).map((s, i) => (
                  <button key={i} disabled={!canManage || s.signed} onClick={() => openSign(r.id, i, s.role_label)}
                    className={cn("flex items-center gap-2 rounded-lg border p-2 text-left text-xs transition-colors",
                      s.signed ? "border-emerald-500/40 bg-emerald-500/5" : "border-border hover:border-primary/50")}
                    data-testid={`sign-${r.id}-${i}`}>
                    {s.signed ? <CheckCircle size={16} weight="fill" className="text-emerald-400 shrink-0" /> : <SignIcon size={16} className="text-muted-foreground shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-semibold">{s.role_label}</div>
                      <div className="text-muted-foreground">{s.signed ? s.name : "Semnează"}</div>
                    </div>
                    {s.signed && s.signature_url && <img src={fileUrl(s.signature_url)} alt="" className="ml-auto h-8 rounded bg-white" />}
                  </button>
                ))}
              </div>
              {canManage && !r.ready_to_ship && (
                <Button size="sm" className="mt-3 h-7 text-xs" disabled={!allSigned} onClick={() => ship(r.id)} data-testid={`ship-${r.id}`}>
                  <Truck size={13} className="mr-1" /> Marchează Ready to Ship
                </Button>
              )}
            </div>
          );
        })}
        {recs.length === 0 && <p className="text-sm text-muted-foreground">Nicio recepție.</p>}
      </div>

      <Dialog open={!!signFor} onOpenChange={(v) => !v && setSignFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Semnătură — {signFor?.role_label}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label>Nume semnatar *</Label>
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="sign-name" placeholder="Nume și prenume" /></div>
            <div><Label className="mb-1.5 block">Semnătură</Label><SignaturePad onCapture={setSignData} /></div>
          </div>
          <DialogFooter><Button onClick={confirmSign} disabled={signing} data-testid="confirm-sign-btn">{signing ? "Se salvează…" : "Confirmă semnătura"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
